import { isValidElement, type ReactNode } from "react";

/**
 * Texto de um aviso (sonner e shadcn): transforma QUALQUER valor num texto que o React
 * aceita como filho, e troca o texto técnico do axios por português.
 *
 * Por que existe: o app passa `error.response.data.detail` direto ao título do aviso. No 422
 * do FastAPI o `detail` é uma LISTA de objetos (`[{type, loc, msg, input}]`); o React não
 * renderiza objeto e lança "Objects are not valid as a React child", e sem ErrorBoundary na
 * raiz a árvore inteira cai. A regra da lista (a primeira `msg`) é a mesma do
 * `mensagemDeErro` em `lib/erroApi.ts`.
 *
 * Função pura, sem dependência do sonner nem do shadcn: quem embrulha cada um chama daqui.
 */

export type TipoAviso = "success" | "error" | "info" | "warning" | "loading" | "message";

export interface OpcoesDoAviso {
  tipo: TipoAviso;
  /** Título: sempre precisa de texto (cai no padrão do tipo). Descrição: pode ficar de fora. */
  obrigatorio: boolean;
}

const PADRAO_POR_TIPO: Record<TipoAviso, string> = {
  error: "Não foi possível concluir. Tente de novo.",
  success: "Pronto.",
  warning: "Atenção.",
  info: "Aviso.",
  loading: "Carregando…",
  message: "Aviso.",
};

const TEXTO_POR_CODIGO: Record<number, string> = {
  400: "pedido inválido (erro 400)",
  401: "sessão expirada — entre de novo (erro 401)",
  403: "sem permissão para isso (erro 403)",
  404: "não encontrado (erro 404)",
  409: "conflito com um registro que já existe (erro 409)",
  413: "arquivo grande demais (erro 413)",
  422: "dados inválidos (erro 422)",
  429: "muitas tentativas — espere um pouco (erro 429)",
};

function textoDoCodigo(codigo: number): string {
  const fixo = TEXTO_POR_CODIGO[codigo];
  if (fixo) return fixo;
  if (codigo >= 500 && codigo <= 599) {
    return `falha no servidor — tente de novo em instantes (erro ${codigo})`;
  }
  return `o servidor respondeu com erro ${codigo}`;
}

// Os três textos que o axios (e o fetch) escrevem quando não há mensagem do backend.
const TRECHO_TECNICO =
  /Request failed with status code (\d+)|Network Error|Failed to fetch|timeout of \d+ms exceeded/g;

function maiuscula(texto: string): string {
  return texto.charAt(0).toUpperCase() + texto.slice(1);
}

/**
 * Troca, em qualquer posição do texto, o jargão do axios/fetch por português. No início do
 * texto a primeira letra vai em maiúscula ("Falha no servidor — …"); no meio fica minúscula
 * ("Erro ao salvar: falha no servidor — …"). Texto sem jargão volta igual.
 */
export function traduzirErroTecnico(texto: string): string {
  return texto.replace(
    TRECHO_TECNICO,
    (trecho: string, codigo: string | undefined, posicao: number) => {
      let traduzido: string;
      if (codigo !== undefined) {
        traduzido = textoDoCodigo(Number(codigo));
      } else if (trecho.startsWith("timeout")) {
        traduzido = "o servidor demorou demais para responder";
      } else {
        traduzido = "sem conexão com o servidor";
      }
      const noInicio = texto.slice(0, posicao).trim() === "";
      return noInicio ? maiuscula(traduzido) : traduzido;
    }
  );
}

const PROFUNDIDADE_MAXIMA = 5;

function textoNaoVazio(valor: unknown): string | undefined {
  if (typeof valor !== "string") return undefined;
  const limpo = valor.trim();
  return limpo ? limpo : undefined;
}

/** Texto cru (sem tradução) que um valor carrega, ou `undefined` se não houver nenhum. */
function extrairTexto(valor: unknown, profundidade: number): string | undefined {
  if (profundidade > PROFUNDIDADE_MAXIMA) return undefined;

  const texto = textoNaoVazio(valor);
  if (texto) return texto;

  if (Array.isArray(valor)) {
    if (valor.length > 0 && valor.every((item) => textoNaoVazio(item) !== undefined)) {
      return valor.map((item) => (item as string).trim()).join("; ");
    }
    // Lista de erros de validação do FastAPI: a primeira mensagem é a útil.
    for (const item of valor) {
      if (item === null || typeof item !== "object") continue;
      const achado = extrairTexto(item, profundidade + 1);
      if (achado) return achado;
    }
    return undefined;
  }

  if (valor !== null && typeof valor === "object" && !isValidElement(valor)) {
    const objeto = valor as { msg?: unknown; message?: unknown; detail?: unknown };
    return (
      textoNaoVazio(objeto.msg) ??
      textoNaoVazio(objeto.message) ??
      extrairTexto(objeto.detail, profundidade + 1)
    );
  }

  return undefined;
}

/**
 * Converte o que o chamador mandou (string, lista do 422, objeto de erro, `Error`, número…)
 * no que o aviso renderiza. Função e elemento React passam como vieram (o sonner aceita
 * título-função). Sem texto aproveitável: o padrão do tipo (`obrigatorio`) ou `undefined`.
 */
export function textoDoAviso(
  valor: unknown,
  opcoes: OpcoesDoAviso
): ReactNode | (() => ReactNode) | undefined {
  const { tipo, obrigatorio } = opcoes;
  const padrao = obrigatorio ? PADRAO_POR_TIPO[tipo] : undefined;

  if (typeof valor === "function") return valor as () => ReactNode;
  if (isValidElement(valor)) return valor;

  if (typeof valor === "number") return Number.isFinite(valor) ? String(valor) : padrao;
  if (typeof valor === "bigint") return String(valor);

  const texto = extrairTexto(valor, 0);
  return texto ? traduzirErroTecnico(texto) : padrao;
}
